<template>
	<header id="site-header">
		<jump-to-content></jump-to-content>
		<a v-bind:href="'/' + lang" v-on:click.stop.prevent="goHome" @keyup.enter="goHome">
			<h1 id="site-name" v-html="formattedSiteName"></h1>
		</a>
		<a v-bind:href="'/' + lang" v-on:click.stop.prevent="goHome" @keyup.enter="goHome" id="site-icon" v-bind:class="[{'animating':animateSiteIcon || showFullLogo}]" @mouseover="animateSiteIconStart" @mouseout="animateSiteIconStop">
			<animated-logo size="small" color="red" v-bind:animated="animateSiteIcon || showFullLogo"></animated-logo>
		</a>
		<div id="header-menu-toggle-buttons">
			<SwapLanguageLink 
				v-if="open && showLanguageLink" 
				:action="'update-menu'" 
				v-on:languageSwapped="updateMenus" />
			<menu-toggle-button 
				v-if="!isSearchPage && open" 
				id="header-search-toggle" 
				v-bind:active="searchVisible" 
				v-on:toggleMenu="toggleSearch" 
				@keydown.enter="toggleSearch" 
				:label="$t('search')" 
				:class="'search'"></menu-toggle-button>
			<menu-toggle-button id="header-menu-toggle" v-bind:active="open" v-on:toggleMenu="toggleMenu" @keydown.enter="toggleMenu"></menu-toggle-button>
		</div>
		<section id="header-menu-wrapper" v-bind:class="['menu', currentLanguageClass, {'open':open, 'animate-in':readyToAnimate, 'articles-showing': articles, 'submenu-active' : selectedParentIndex}]">
			<SwapLanguageLink 
				v-if="showLanguageLink" 
				:action="'update-menu'" 
				v-on:languageSwapped="updateMenus" />
			<QuickSearch v-if="!isSearchPage && searchVisible" />
			<nav v-if="headerMenu.main" class="row small-up-1 medium-up-2 large-up-4">
				<div 
					v-for="(chapter, index) in headerMenu.main" 
					:key="'c' + index" 
					class="column">
					<h3 
						v-if="chapter"
						tabindex="0" 
						:class="[{ 'active' : selectedParentIndex===index }]" @keyup.enter="toggleMenuList(index)" 
						v-on:click="toggleMenuList(index)" 
						v-text="chapter.title"></h3>
					<div class="menu-list" :class="[{
						'list-open' : selectedParentIndex===index,
						'list-closed' : selectedParentIndex!==index
					}]">
						<a 
							href="#" 
							aria-role="button" 
							tabindex= "0" 
							class="button go-to go-back has-icon icon-right" 
							v-on:click.stop.prevent="toggleMenuList(index)" @keyup.enter="toggleMenuList(index)" >{{$t('back')}}</a>
							<text-link v-for="(article, articleIndex) in chapter.children" 
								:key="'c' + index + '-a' + articleIndex" 
								:model="article" 
								:class="[{ active : (currentPath==article.url) }]" 
								v-on:click.native="closeMenuIfActivePage(article.url)"
								:tabindex="tabindex"></text-link>
					</div>
				</div>
			</nav>
			<div :class="['secondary-links', {'submenu-active' : selectedParentIndex}]">
				<download-button 
					v-if="currentLanguageClass==='no'" 
					mode="default" 
					:tabindex="tabindex" 
					:lang="lang">
				</download-button>
				<text-link v-if="headerMenu && headerMenu.statisticsLink"  
					:model="headerMenu.statisticsLink" 
					:class="['button statistics has-icon icon-right', { active : (currentPath==headerMenu.statisticsLink.url) }]" 
					v-on:click.native="closeMenuIfActivePage(headerMenu.statisticsLink.url)"
					:tabindex="tabindex"></text-link>
			</div>
		</section>
	</header>
</template>

<script>
import { mapState } from 'vuex'
import TextLink from './TextLink'
import DownloadButton from 'RuterSharedBlocks/downloads/ButtonToDownloads'
import MenuToggleButton from './MenuToggleButton'
import AnimatedLogo from './AnimatedLogo'
import JumpToContent from 'dc-vue-default/src/components/global/header/JumpToContent'
import DOMPurify from 'dompurify';

export default {
	props: ['headerMenu'],
	data() {
		return {
			open: false,
			searchVisible: false,
			readyToAnimate: false,
			animateSiteIcon: false,
			showFullLogo: true,
			articles: false,
			selectedIndex: false,
			ruter: 'Ruter',
			currentLanguage: false,
			currentPath: false,
			selectedParentIndex: false,
		}
	},
	computed: {
		...mapState({
			lang:  state => state.lang,
			siteName: state => state.siteName
		}),
		currentLanguageClass() {
			return this.currentLanguage;
		},
		formattedSiteName() {
			if (typeof this.siteName === 'undefined' || this.siteName === '') return '';
			return DOMPurify.sanitize(this.$options.filters.boldNumbers(this.siteName))
		},
		tabindex() {
			return ((this.open) ? '0' : -1)
		},
		backTabindex() {
			return (this.article) ? 0 : -1
		},
		toggleIcon() {
			return '<span class="toggle-icon"></span>'
		},
		isSearchPage() {
			return (this.$route.name==='no-search'||this.$route.name==='en-search')
		},
		showLanguageLink() {
			return (typeof this.headerMenu.languageLink !== 'undefined' && this.headerMenu.languageLink)
		}
	},
	methods: {
		animateSiteIconStart() {
			this.animateSiteIcon = true;
		},
		animateSiteIconStop() {
			this.animateSiteIcon = false;
		},
		toggleMenu() {
			if(this.open === false) {
				this.open = true;
				window.addEventListener("keydown", this.closeMenuWithEsc)
			} else {
				this.open = false;
				window.removeEventListener("keydown", this.closeMenuWithEsc)
			}
			// this.open = !this.open;
		},
		toggleSearch() {
			this.searchVisible = !this.searchVisible
		},
		toggleMenuList(index) {
			this.selectedParentIndex = (this.selectedParentIndex===index) ? false : index;
		},
		closeMenu() {
			if (this.currentLanguage === this.lang) {
				this.open = false;
			} else {
				this.currentLanguage = this.lang;
			}
		},
		closeMenuIfActivePage(url) {
			if (this.currentPath==url) {
				this.closeMenu()
			} 
		},
		resetHeader() {
			setTimeout(() => {
				this.closeMenu();
				this.showFullLogo = true;
				this.searchVisible = false;
			}, 500);
		},
		updateMenus(data) {
			this.hideArticles();
			this.currentLanguage = data.lang;
			this.$emit('updateMenus', data);
		},
		showArticles(data) {
			this.selectedIndex = data.index;
			this.articles = this.headerMenu.main[data.index].children || false;
			if (typeof data.keyEvent !== 'undefined' && data.keyEvent) this.setArticleFocus()
		},
		setArticleFocus() {
			var links = document.querySelector('.behind-red').getElementsByTagName('a');
			if (typeof links[1] === 'undefined') {
				setTimeout(() => { this.setArticleFocus() }, 500)
				return;
			}
			setTimeout(() => { links[1].focus(); }, 150)
		},
		hideArticles() {
			this.articles = false;
		},
		goHome() {
			var name = this.lang + '-home';
			this.$router.push({
				name: name
			});
		},
		handleScroll() {
			this.showFullLogo = (window.pageYOffset<=(window.innerHeight*.3));
		},
		updateCurrentPath() {
			this.currentPath = window.location.origin + this.$route.path;
		},
		closeMenuWithEsc(e) {
			if (e.keyCode == 27) {
				this.closeMenu();
			}
		}
	},
	mounted() {
		this.updateCurrentPath()
		window.addEventListener('scroll', this.handleScroll)
	},
	beforeDestroy() {
		window.removeEventListener('scroll', this.handleScroll)
	},
	watch: {
		open: function(newVal) {
			if (newVal) {
				this.currentLanguage = this.lang
				document.body.classList.add('menu-open');
				setTimeout(() => this.readyToAnimate = true, 150);
			} else {
				document.body.classList.remove('menu-open');
				setTimeout(() => this.selectedIndex = false, 650);
				setTimeout(() => this.readyToAnimate = false, 650);
				setTimeout(() => this.articles = false, 650);
			}
		},
		'$route': function() {
			this.updateCurrentPath()
			this.resetHeader();
		},
		
	},
	components: {
		TextLink,
		DownloadButton,
		MenuToggleButton,
		AnimatedLogo,
		JumpToContent,
		QuickSearch: () => import('./QuickSearch'),
		SwapLanguageLink: () => import('./SwapLanguageLink')
	}
}
</script>

<style lang="scss">
  @import "../../styles/scss/global/vars/header";
  @import "../../styles/scss/global/header";
  @import "../../styles/scss/global/header/menu";
  @import "../../styles/scss/component/article-menu";
</style>